<template>
  <Button v-slot="slotProps" asChild :aria-label="t('viewDetailsForStrike', { label: strike.strike_label })">
    <div
      ref="containerRef"
      v-bind="slotProps.a11yAttrs"
      :class="[
        'single-strike-card-row cursor-pointer flex-1 flex flex-col hover:bg-(--p-surface-50) dark:hover:bg-(--p-surface-800) transition-colors',
        props.class || '',
      ]"
      @click="handleStrikeClick"
    >
      <div class="flex items-center justify-between px-4 pt-[17px] pb-[13px]">
        <div class="flex flex-col gap-[2px] flex-1 min-w-0">
          <p
            class="font-semibold text-[16px] leading-[20px] truncate"
            :class="{
              'text-democrat': strike.party === 'Democrat',
              'text-republican': strike.party === 'Republican',
            }"
          >
            {{ strike.strike_label }}
            <i
              v-if="strike.restricted"
              v-tooltip.top="t('restricted')"
              class="ibi-privacy-eye-hide text-sm text-muted-color cursor-help font-normal"
            />
          </p>
          <p class="text-xs text-muted-color leading-[16px] truncate">{{ strike.measured_period }}</p>
        </div>
        <PositionBadge :conids="allStrikeConids" useTextLayout />
      </div>
      <IllustratedBidAsk
        v-if="isSessionVisible"
        :yesConid="strike.conid_yes"
        :noConid="strike.conid_no"
        :yesConidExPairs="strike.yesConidExPairs"
        :noConidExPairs="strike.noConidExPairs"
        :exchange="exchange"
        :multi-exchange="multiExchange"
        :priceSide="priceSide"
        :product-conid="productConid"
        :routing-exchange="routingExchange"
        :isLive="isVisible"
        :seedAskYes="strike.ask_yes"
        :seedAskNo="strike.ask_no"
        :seedBidYes="strike.bid_yes"
        :seedBidNo="strike.bid_no"
        :closed="isStrikeClosed(strike)"
      />
    </div>
  </Button>
</template>

<script setup lang="ts">
import type { ForecastTraderStrike } from '@/types/forecasttrader.types';
import IllustratedBidAsk from './IllustratedBidAsk.vue';
import PositionBadge from './PositionBadge.vue';
import useContractActions from '@/composables/useContractActions';
import { useElementVisibility } from '@vueuse/core';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSessionVisibility } from '@/composables/useSessionVisibility';
import { useAppState } from '@/stores/useAppState';
import { conidExPairsToPositionConids } from '@/utils/conidExPairs';
import { isStrikeClosed } from '@/utils/marketClosed';

const { t } = useI18n();
const appState = useAppState();

const { isSessionVisible } = useSessionVisibility();
const actions = useContractActions();

const containerRef = ref<HTMLDivElement>();
const isVisible = useElementVisibility(containerRef);

interface StrikeCardRowProps {
  strike: ForecastTraderStrike;
  exchange: string;
  multiExchange?: boolean;
  productConid: number;
  /** Exchange paired with productConid for routing. Defaults to exchange. */
  routingExchange?: string;
  class?: string | false;
  origin: string;
  priceSide?: 'bid' | 'ask';
  focusStrikeOnClick?: boolean;
}

const props = defineProps<StrikeCardRowProps>();

const allStrikeConids = computed(() =>
  conidExPairsToPositionConids(
    props.strike.conid_yes,
    props.strike.conid_no,
    props.strike.yesConidExPairs,
    props.strike.noConidExPairs,
  ),
);

const handleStrikeClick = () => {
  window.scrollTo(0, 0);
  if (appState.isScrolling) {
    return;
  }
  window.$apm?.logUserInteractionEvent(
    'Card Click',
    'contract-click',
    { conid_yes: `${props.strike.conid_yes}`, origin: props.origin },
    false,
  );
  if (props.focusStrikeOnClick) {
    actions.openQuoteDetails(props.productConid, props.routingExchange ?? props.exchange, props.strike.conid_yes);
  } else {
    actions.openQuoteDetails(props.productConid, props.routingExchange ?? props.exchange);
  }
};
</script>

<style scoped>
.single-strike-card-row {
  contain: layout style paint;
}
</style>
<!-- last modified by AI tools on 2026-10-13 -->
